import { KeyValue, NgClass, DatePipe, KeyValuePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

import { IEditDocumentSingleFormField } from '../../../interfaces/edit-document.interface';

@Component({
  selector: 'app-edit-doc-form-section-content',
  templateUrl: './edit-doc-form-section-content.component.html',
  styleUrls: ['./edit-doc-form-section-content.component.scss'],
  imports: [NgClass, ReactiveFormsModule, FormsModule, DatePipe, KeyValuePipe],
})
export class EditDocFormSectionContentComponent implements OnInit {
  @Input() formSection: any[];
  @Input() fieldValues: any;
  @Input() changedFieldKeys: any[];
  @Input() disableEditing: boolean;
  @Input() inputIdHighlightActive: string;

  @Output() inputChangedEmitter = new EventEmitter<Event>();
  @Output() onInputClickEmitter = new EventEmitter<MouseEvent>();
  @Output() rowAddedEmitter = new EventEmitter<any>();

  /* Input fields grid rows and column count */
  gridRowsCount = 0;
  gridColumnsCount = 0;
  /* Store the RowNumber of the last row which is visible for the user */
  lastVisibleRowNumber = 0;
  today = new Date().toJSON().split('T')[0];

  /**
   * getGridDimensions will update rows and columns count to display dividers and buttons
   * in relevant places
   * @returns void
   */
  getGridDimensions(): void {
    if (!this.formSection[0]?.isGrid) return; /* defence for errors */
    this.gridRowsCount = this.formSection[0].rowsCount;
    this.gridColumnsCount = this.formSection[0].columnCount;
  }

  /**
   * updateLastVisibleRow() will update the number of the last row that is visible to the user
   * It's required so that the divider between rows can be displayed along with the button to add rows
   * @returns void
   */
  updateLastVisibleRowNumber(): void {
    if (!this.formSection[0]?.isGrid) return; /* defence for errors */
    for (const key in this.formSection[1]) {
      const currentIterationField: IEditDocumentSingleFormField = this.formSection[1][key];
      if (
        currentIterationField.isHidden &&
        currentIterationField.RowNumber > this.lastVisibleRowNumber
      )
        this.lastVisibleRowNumber = currentIterationField.RowNumber;
    }
  }

  /**
   * preserve order of fields by SectionMappingOrder property in form
   * @param akv key-value pair to be compared
   * @param bkv key-value pair to be compared
   * @returns number
   */
  indexOrderAsc = (
    akv: KeyValue<string, IEditDocumentSingleFormField>,
    bkv: KeyValue<string, IEditDocumentSingleFormField>,
  ): number => {
    const a = akv.value.DocumentSectionMappingOrder;
    const b = bkv.value.DocumentSectionMappingOrder;
    return a > b ? 1 : b > a ? -1 : 0;
  };

  /**
   * getValue accesses the value from the prop and returns it.
   * This is required for the value property in the input to work, for some reason.
   * @param prop
   * @returns
   */
  getValue(prop: any, value?: any): string {
    if (!value) return prop.value?.DataValue;
    return prop[value];
  }

  /**
   * onInputChange() will notify parent that input value changed and update the source data
   * @param event is an input event passed in by input itself
   */
  onInputChange(event: Event) {
    this.inputChangedEmitter.emit(event);
  }

  /**
   * onCheckboxChange() will notify parent that checkbox value changed
   * @param event is an input event passed in by checkbox input
   */
  onCheckboxChange(event: Event) {
    this.inputChangedEmitter.emit(event);
  }

  /**
   * onInputClick() will notify parent that specific input has been clicked to update highlights
   * @param event is a MouseEvent sent by input when clicked on
   */
  onInputClick(event: MouseEvent) {
    this.onInputClickEmitter.emit(event);
  }

  /**
   * onCheckboxClick() runs every time when checkbox is clicked -
   * checkbox can't be disabled as the clicks are ignored so the checkbox
   * value change is prevented if editing is disabled
   * @param event is the event that happens on the checkbox click
   */
  onCheckboxClick(event: MouseEvent) {
    if (this.disableEditing) event.preventDefault();
    this.onInputClickEmitter.emit(event);
  }

  /**
   * showNextGridRow() will access field with given RowNumber and update its display property
   * so that it can be shown in the UI. FieldValues CustomCSS properties with custom CSS strings
   * also need to be updated to show fields in PDF due to duplicated data source for both sides of edit doc
   * @param lastFieldInRow is the last field that indicates the end of one row and start of the next one
   * TODO: The Edit Doc formFields and data in general should come from one service shared between the right side and left (pdf) side
   * This would allow for cleaner code in the components/services and most importantly serve data from one source
   * In this case, two loops are required - formSection is comming from summaryData which is a data source for right side
   * and formFields which is a data source for pdf
   */
  showNextGridRow(lastFieldInRow: IEditDocumentSingleFormField) {
    const { RowNumber: lastVisibleRowNumber } = lastFieldInRow;
    for (const key in this.formSection[1]) {
      const currentIterationField: IEditDocumentSingleFormField = this.formSection[1][key];
      if (lastVisibleRowNumber + 1 === currentIterationField.RowNumber)
        currentIterationField.isHidden = true;
      this.updateLastVisibleRowNumber();
    }
    /* Duplicated source - fieldValues for PDF, formSection (summaryData) for the right side */
    for (const key in this.fieldValues)
      if (this.fieldValues[key].RowNumber === lastVisibleRowNumber + 1) {
        const tempCustomCss = this.fieldValues[key].CustomCSS;
        const parsedCss = JSON.parse(tempCustomCss);
        parsedCss.display = 'inline-block';
        this.fieldValues[key].CustomCSS = JSON.stringify(parsedCss);
      }

    this.rowAddedEmitter.emit(lastFieldInRow.RowNumber);
  }

  ngOnInit(): void {
    this.getGridDimensions();
    this.updateLastVisibleRowNumber();
  }
}
